import React from 'react';

function AboutIntro() {
  return (
      <section className="mb-16">
        {/* Introduction */}
        <div className="flex flex-col md:flex-row items-start justify-center gap-12 mb-16">
          <div className="md:w-1/2 text-text-gray">
            <h2 className="text-3xl font-semibold text-text-white mb-6">
              My Journey as a Software Developer
            </h2>

            <p className="mb-4 text-lg leading-relaxed">
              My journey into software development did not begin with a computer science degree,
              a corporate job, or a carefully planned career path. It started with a simple
              conversation when I was in <strong className="text-text-white">Class 9</strong>.
            </p>

            <p className="mb-4 text-lg leading-relaxed">
              One day, my neighbour noticed that I was genuinely interested in computers and told
              my mother:
            </p>

            <blockquote className="border-l-4 border-red-500 pl-4 mb-4 italic text-text-gray">
              “Aunty, Rishabh is interested in computers. You should get him started with programming.”
            </blockquote>

            <p className="mb-4 text-lg leading-relaxed">
              At that time, I had no idea that this small suggestion would eventually become the
              foundation of my career.
            </p>

            <p className="mb-4 text-lg leading-relaxed">
              At the beginning of Class 9, I started learning{' '}
              <strong className="text-text-white">C++</strong> and completed my first programming
              certification. Once I understood the basic idea of programming, I became curious
              about other languages. Later that same year, I completed a{' '}
              <strong className="text-text-white">Java certification</strong>.
            </p>

            <p className="text-lg leading-relaxed">
              Then, in Class 10, I learned <strong className="text-text-white">PHP</strong> and
              completed another certification. At that stage, programming was primarily an
              interest for me. I wasn't thinking about becoming a professional software developer.
              I simply enjoyed understanding how software worked and figuring out how to make
              things happen through code.
            </p>
          </div>

          <div className="md:w-1/2">
            <img
                src="/images/rishabh-gupta.jpg"
                alt="Rishabh Gupta - Software Developer"
                className="rounded-lg shadow-lg w-full h-auto object-cover"
            />
          </div>
        </div>

        {/* Full Width Career Journey */}
        <div className="w-full text-text-gray">

          <h3 className="text-2xl font-semibold text-text-white mb-4">
            My First Real Development Project
          </h3>

          <p className="mb-4 text-lg leading-relaxed">
            In <strong className="text-text-white">2014</strong>, I started college.
          </p>

          <p className="mb-4 text-lg leading-relaxed">
            During college, one of my friends approached me with an opportunity. They needed
            a project for their practical examination and asked whether I could build it.
          </p>

          <p className="mb-4 text-lg leading-relaxed">
            I decided to use Java. Instead of creating a very basic academic project, I tried
            to make it fairly dynamic and functional.
          </p>

          <p className="mb-6 text-lg leading-relaxed">
            That became my <strong className="text-text-white">first real development project</strong>.
          </p>

          <p className="mb-6 text-lg leading-relaxed">
            After that, through references, I started receiving more project opportunities.
            I built multiple projects during college using{' '}
            <strong className="text-text-white">Core Java and MySQL</strong>. These projects were
            extremely important because they gave me the opportunity to move beyond certifications
            and actually solve practical problems.
          </p>

          <p className="mb-8 text-lg leading-relaxed">
            I started learning how to understand requirements, design application logic,
            structure code, work with databases, debug unexpected behavior, and convert an
            idea into a working application.
          </p>

          <h3 className="text-2xl font-semibold text-text-white mb-4">
            Entering the Software Industry in 2018
          </h3>

          <p className="mb-4 text-lg leading-relaxed">
            In <strong className="text-text-white">2018</strong>, I started actively looking
            for software development opportunities. At that time, I was exploring both{' '}
            <strong className="text-text-white">Java and PHP</strong>.
          </p>

          <p className="mb-4 text-lg leading-relaxed">
            Eventually, I got my first job opportunity.
          </p>

          <p className="mb-4 text-lg leading-relaxed">
            There was an interesting moment during that interview that I still remember.
            I was asked to write PHP code.
          </p>

          <p className="mb-4 text-lg leading-relaxed">
            The problem was that I didn't even know what the extension of a PHP source file was.
          </p>

          <p className="mb-4 text-lg leading-relaxed">
            Instead of pretending that I knew everything, I searched for the information using
            Google, understood what I needed, and continued writing the code.
          </p>

          <blockquote className="border-l-4 border-red-500 pl-4 mb-6 italic text-text-gray">
            “Rishabh, you don't know how to write the code properly yet, but you have good
            logical and problem-solving skills. Based on that, I am selecting you.”
          </blockquote>

          <p className="mb-8 text-lg leading-relaxed">
            That was one of the earliest lessons of my professional career:{' '}
            <strong className="text-text-white">
              knowing a programming language is important, but knowing how to think is even more important.
            </strong>
          </p>

          <h3 className="text-2xl font-semibold text-text-white mb-4">
            From WordPress-Based Development to Custom Software Development
          </h3>

          <p className="mb-4 text-lg leading-relaxed">
            After my first company, I joined my second organization. When I joined, I discovered
            that most of the company's development work was being done using WordPress plugins.
            There was very little custom programming involved.
          </p>

          <p className="mb-4 text-lg leading-relaxed">
            Soon, an opportunity came up that changed that.
          </p>

          <p className="mb-4 text-lg leading-relaxed">
            A project belonging to a relative of the company's CEO was stuck because the
            requirements could not be implemented properly using the existing WordPress-based
            approach.
          </p>

          <p className="mb-4 text-lg leading-relaxed">
            I believed that the problem could be solved through custom development. I told them
            that I could build the required functionality myself, but asked them for one thing:
          </p>

          <blockquote className="border-l-4 border-red-500 pl-4 mb-4 italic text-text-gray">
            “Give me one month without asking for implementation updates.”
          </blockquote>

          <p className="mb-4 text-lg leading-relaxed">
            I wanted enough time to properly understand the requirements, design the solution,
            and build it without constantly changing direction.
          </p>

          <p className="mb-4 text-lg leading-relaxed">
            They agreed. I spent the next month working on the solution.
          </p>

          <p className="mb-6 text-lg leading-relaxed">
            After one month, I demonstrated the completed implementation. The response was simple:
            <strong className="text-text-white"> “This is exactly what we needed.”</strong>
          </p>

          <p className="mb-8 text-lg leading-relaxed">
            That project helped demonstrate that custom development could solve problems that
            the existing WordPress-based approach could not. The company subsequently expanded
            its development team, and I received opportunities to work on more custom software
            development projects.
          </p>

          <h3 className="text-2xl font-semibold text-text-white mb-4">
            The Debugging Problem That Led Me Back to Java
          </h3>

          <p className="mb-4 text-lg leading-relaxed">
            Later, I joined my third company as a{' '}
            <strong className="text-text-white">PHP Developer</strong>.
          </p>

          <p className="mb-4 text-lg leading-relaxed">
            A few months after joining, I was sitting with my friends having tea when a person
            named Arif approached my friend Sushil.
          </p>

          <p className="mb-4 text-lg leading-relaxed">
            Arif was facing a serious problem in his project. He had already tried getting help
            from multiple people and had spent several nights trying to debug the issue, but he
            still couldn't identify the root cause.
          </p>

          <p className="mb-4 text-lg leading-relaxed">
            Sushil suggested that he show the problem to me.
          </p>

          <p className="mb-4 text-lg leading-relaxed">
            Initially, I couldn't understand what was happening either. But instead of trying
            random fixes, I started breaking the problem down.
          </p>

          <p className="mb-4 text-lg leading-relaxed">
            I looked at the flow step by step. I tried to understand what was supposed to happen,
            what was actually happening, and where the expected behavior changed.
          </p>

          <p className="mb-4 text-lg leading-relaxed">
            After investigating the issue, I suggested a possible solution. After a few attempts,
            the issue was resolved.
          </p>

          <p className="mb-4 text-lg leading-relaxed">
            Around the same time, Arif's manager contacted the team lead to ask whether the issue
            had finally been resolved.
          </p>

          <blockquote className="border-l-4 border-red-500 pl-4 mb-4 italic text-text-gray">
            “Who is this guy?”
          </blockquote>

          <p className="mb-4 text-lg leading-relaxed">
            The team lead explained that I was the person who had helped resolve the issue.
            The manager then asked which manager I worked under.
          </p>

          <p className="mb-8 text-lg leading-relaxed">
            After I told him, he offered me an opportunity to work on{' '}
            <strong className="text-text-white">Java</strong>. I accepted the opportunity,
            and from that point onward, I started working professionally as a Java developer.
          </p>

          <h3 className="text-2xl font-semibold text-text-white mb-4">
            Solving a UAT Issue Without Direct Server Access
          </h3>

          <p className="mb-4 text-lg leading-relaxed">
            One of the most interesting problems I encountered involved an application that
            behaved differently across environments.
          </p>

          <p className="mb-4 text-lg leading-relaxed">
            The application worked correctly on my local machine and staging environment,
            but there was an issue on UAT.
          </p>

          <p className="mb-4 text-lg leading-relaxed">
            I had already implemented logging, but I couldn't access the logs because they
            were being generated inside containers. Developers weren't given direct access
            because of security and operational concerns.
          </p>

          <p className="mb-4 text-lg leading-relaxed">
            So I started thinking about the problem differently.
          </p>

          <blockquote className="border-l-4 border-red-500 pl-4 mb-4 italic text-text-gray">
            “If I can't access the server directly, what can I access?”
          </blockquote>

          <p className="mb-4 text-lg leading-relaxed">
            The answer was my application code, which was already running inside that environment.
          </p>

          <p className="mb-4 text-lg leading-relaxed">
            I used file handling and created a controlled endpoint that allowed me to retrieve
            the required log information through the application itself.
          </p>

          <p className="mb-8 text-lg leading-relaxed">
            This gave me the visibility I needed to investigate the issue and ultimately resolve it.
          </p>

          <h3 className="text-2xl font-semibold text-text-white mb-4">
            Solving a Long-Standing Media Library Problem
          </h3>

          <p className="mb-4 text-lg leading-relaxed">
            Another significant challenge involved a client's media library.
          </p>

          <p className="mb-4 text-lg leading-relaxed">
            The application allowed clients to reuse the same media asset across multiple modules.
            For example, <strong className="text-text-white">ABC.mp3</strong> could be used in
            Module 1, Module 2, and Module 3.
          </p>

          <p className="mb-4 text-lg leading-relaxed">
            If the client updated ABC.mp3, the updated version should have been reflected
            everywhere. However, that wasn't happening.
          </p>

          <p className="mb-4 text-lg leading-relaxed">
            I investigated the issue and discovered that the existing implementation was creating
            <strong className="text-text-white"> duplicate database records and duplicate
              file-storage entries</strong> instead of maintaining a shared reference to the same
            media asset.
          </p>

          <p className="mb-4 text-lg leading-relaxed">
            The real problem was therefore not simply a bug. It was a{' '}
            <strong className="text-text-white">data-modeling and architecture problem</strong>.
          </p>

          <p className="mb-4 text-lg leading-relaxed">
            I proposed modifying the database structure so that the media asset could exist as
            a single source of truth while multiple modules could reference it.
          </p>

          <p className="mb-4 text-lg leading-relaxed">
            However, there was already existing production data, so the migration had to be
            handled carefully.
          </p>

          <p className="mb-4 text-lg leading-relaxed">
            I explained the technical risks to the project manager. The client then requested
            to speak with me directly before approving the change.
          </p>

          <p className="mb-4 text-lg leading-relaxed">
            I explained the complete problem, the proposed architecture, the migration approach,
            the risks, and how we would validate the migration before production.
          </p>

          <p className="mb-4 text-lg leading-relaxed">
            After the client approved the approach, I restored the production database into
            UAT and staging environments and developed the migration script locally.
          </p>

          <p className="mb-4 text-lg leading-relaxed">
            I tested and refined the migration multiple times until I was able to preserve
            the existing data successfully.
          </p>

          <p className="mb-4 text-lg leading-relaxed">
            The changes were deployed to UAT, where the client completed testing and approved
            the production deployment.
          </p>

          <p className="mb-8 text-lg leading-relaxed">
            After production deployment, the client and their team sent appreciation emails
            and even sent me a gift. The experience was particularly meaningful because the
            issue had existed in the project for a considerable amount of time.
          </p>

          <h3 className="text-2xl font-semibold text-text-white mb-4">
            Improving API Performance by More Than 90%
          </h3>

          <p className="mb-4 text-lg leading-relaxed">
            In another organization, I encountered a completely different challenge.
          </p>

          <p className="mb-4 text-lg leading-relaxed">
            An API was processing increasingly large amounts of input data, and as the input
            size increased, the processing time increased dramatically.
          </p>

          <p className="mb-4 text-lg leading-relaxed">
            In some cases, the API took <strong className="text-text-white">more than one minute</strong>
            to return a response.
          </p>

          <p className="mb-4 text-lg leading-relaxed">
            I analyzed the implementation, identified the performance bottlenecks, and optimized
            the API.
          </p>

          <p className="mb-8 text-lg leading-relaxed">
            The same processing that previously took more than a minute was brought down to
            <strong className="text-text-white"> less than three seconds</strong>, resulting
            in a <strong className="text-text-white">90%+ improvement in response time</strong>.
          </p>

          <h3 className="text-2xl font-semibold text-text-white mb-4">
            What My Journey Has Taught Me
          </h3>

          <p className="mb-4 text-lg leading-relaxed">
            When I look back at my career, I don't see a straight path from one programming
            language to another.
          </p>

          <p className="mb-4 text-lg leading-relaxed">
            I see a series of problems — and every problem taught me something.
          </p>

          <p className="mb-4 text-lg leading-relaxed">
            I started with C++ in Class 9. Then Java. Then PHP. I built projects during college.
            I entered the industry without knowing everything. I learned through documentation,
            Google, experimentation, and real-world problems.
          </p>

          <p className="mb-4 text-lg leading-relaxed">
            I moved from WordPress-based development to custom development when an existing
            approach couldn't solve a business requirement. I moved from PHP to Java after
            solving a difficult debugging problem.
          </p>

          <p className="mb-4 text-lg leading-relaxed">
            I learned how to investigate environment-specific issues when I didn't have direct
            infrastructure access. I worked on database architecture when a long-standing media
            library problem turned out to be a data-modeling issue.
          </p>

          <p className="mb-4 text-lg leading-relaxed">
            I handled data migration while taking responsibility for preserving existing business
            data, communicated technical risks and solutions directly with clients, and optimized
            an API that was taking more than a minute to respond and brought it down to less than
            three seconds.
          </p>

          <h3 className="text-2xl font-semibold text-text-white mb-4">
            My Engineering Philosophy
          </h3>

          <p className="mb-4 text-lg leading-relaxed">
            These experiences have shaped how I approach software development today.
          </p>

          <p className="mb-4 text-lg leading-relaxed">
            I don't believe a developer needs to know everything before taking ownership of a problem.
          </p>

          <p className="mb-4 text-lg leading-relaxed">
            I believe in:
          </p>

          <p className="mb-6 text-lg leading-relaxed text-text-white font-medium">
            Understand → Investigate → Learn → Experiment → Solve → Validate → Take Ownership
          </p>

          <p className="mb-4 text-lg leading-relaxed">
            Programming languages can be learned. Frameworks can be learned. Tools can be learned.
            But the ability to break down an unfamiliar problem, investigate the root cause,
            think creatively, and take ownership of the solution is what makes a developer valuable.
          </p>

          <p className="mb-4 text-lg leading-relaxed">
            Some of the biggest opportunities in my career came from problems that initially
            looked unfamiliar or difficult.
          </p>

          <p className="text-lg leading-relaxed text-text-white font-medium">
            I may not always know the answer immediately. But I know how to find it.
          </p>
        </div>
      </section>
  );
}

export default AboutIntro;